import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
  Plus,
  Lightbulb,
  Bug,
  TrendingUp,
  Trash2,
  MessageSquare,
  Clock,
  CheckCircle2,
  XCircle,
  RefreshCw,
  User,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_CONFIG = {
  sugerencia: { label: 'Sugerencia', icon: Lightbulb, color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
  bug:        { label: 'Error / Bug', icon: Bug,       color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' },
  mejora:     { label: 'Mejora',      icon: TrendingUp, color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' },
};

const STATUS_CONFIG = {
  pendiente:    { label: 'Pendiente',    color: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200' },
  en_revision:  { label: 'En revisión', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' },
  implementado: { label: 'Implementado', color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
  rechazado:    { label: 'Rechazado',   color: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200' },
};

const PRIORITY_CONFIG = {
  baja:  { label: 'Baja',  color: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' },
  media: { label: 'Media', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300' },
  alta:  { label: 'Alta',  color: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' },
};

// ─── KPI Card (misma estructura que StatCard en DashboardPage) ────────
const KpiCard = ({ title, value, icon: Icon, color = 'primary', onClick, active }) => {
  const colorClasses = {
    primary:   'bg-[#1a365d] text-white',
    secondary: 'bg-[#2d8659] text-white',
    accent:    'bg-[#6b7280] text-white',
    warning:   'bg-amber-500 text-white',
  };
  return (
    <Card
      className={`overflow-hidden cursor-pointer transition-all hover:shadow-md ${active ? 'ring-2 ring-[#1a365d]' : ''}`}
      onClick={onClick}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-3xl font-bold tracking-tight">{value}</p>
          </div>
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Panel de revisión inline (admin) ────────────────────────────────
function ReviewPanel({ item, onSave, onCancel }) {
  const [editStatus, setEditStatus] = useState(item.status);
  const [editNotes, setEditNotes] = useState(item.admin_notes || '');
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      await onSave(item.id, editStatus, editNotes);
      onCancel();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="border-t border-border mt-3 pt-3 space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <p className="text-xs text-muted-foreground mb-1">Estado</p>
          <Select value={editStatus} onValueChange={setEditStatus}>
            <SelectTrigger className="h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-1">Nota para el autor</p>
          <input
            className="w-full h-8 text-sm border border-input rounded-md px-2 bg-background text-foreground"
            placeholder="Opcional..."
            value={editNotes}
            onChange={e => setEditNotes(e.target.value)}
          />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel}>Cancelar</Button>
        <Button size="sm" disabled={saving} onClick={handleSave}>
          {saving ? 'Guardando...' : 'Guardar cambios'}
        </Button>
      </div>
    </div>
  );
}

// ─── Fila de sugerencia en tabla ──────────────────────────────────────
function SugerenciaRow({ item, isAdmin, onStatusChange, onDelete }) {
  const [showReview, setShowReview] = useState(false);
  const TypeIcon = TYPE_CONFIG[item.type]?.icon || Lightbulb;
  const typeConf = TYPE_CONFIG[item.type] || TYPE_CONFIG.sugerencia;
  const statusConf = STATUS_CONFIG[item.status] || STATUS_CONFIG.pendiente;
  const priorityConf = PRIORITY_CONFIG[item.priority] || PRIORITY_CONFIG.media;

  return (
    <>
      <TableRow>
        <TableCell>
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${typeConf.color}`}>
              <TypeIcon className="h-3 w-3" />
              {typeConf.label}
            </span>
          </div>
        </TableCell>
        <TableCell>
          <div>
            <p className="font-medium text-sm">{item.title}</p>
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{item.description}</p>
          </div>
        </TableCell>
        <TableCell>
          <Badge className={priorityConf.color}>{priorityConf.label}</Badge>
        </TableCell>
        <TableCell>
          <Badge className={statusConf.color}>{statusConf.label}</Badge>
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <User className="h-3.5 w-3.5" />
            {item.user_name}
          </div>
        </TableCell>
        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
          {new Date(item.created_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-1">
            {isAdmin && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                title="Revisar"
                onClick={() => setShowReview(v => !v)}
              >
                <MessageSquare className={`h-4 w-4 ${showReview ? 'text-[#1a365d]' : 'text-muted-foreground'}`} />
              </Button>
            )}
            {(isAdmin || item.status === 'pendiente') && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                title="Eliminar"
                onClick={() => onDelete(item.id)}
              >
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            )}
          </div>
        </TableCell>
      </TableRow>

      {/* Panel de revisión expandido */}
      {isAdmin && showReview && (
        <TableRow>
          <TableCell colSpan={7} className="bg-muted/40 py-3 px-4">
            {item.admin_notes && !showReview ? (
              <p className="text-xs text-muted-foreground">
                <span className="font-medium">Nota:</span> {item.admin_notes}
              </p>
            ) : (
              <ReviewPanel
                item={item}
                onSave={onStatusChange}
                onCancel={() => setShowReview(false)}
              />
            )}
          </TableCell>
        </TableRow>
      )}

      {/* Nota del admin (solo lectura, visible para el autor) */}
      {!isAdmin && item.admin_notes && (
        <TableRow>
          <TableCell colSpan={7} className="bg-blue-50 dark:bg-blue-950 py-2 px-4">
            <p className="text-xs text-blue-700 dark:text-blue-300">
              <span className="font-medium">Respuesta del equipo:</span> {item.admin_notes}
            </p>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// ─── Modal nueva sugerencia ───────────────────────────────────────────
function NewFeedbackModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ type: 'sugerencia', title: '', description: '', priority: 'media' });
  const [saving, setSaving] = useState(false);

  async function handleSubmit() {
    if (!form.title.trim() || !form.description.trim()) {
      toast.error('El título y la descripción son obligatorios');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/feedback`, form);
      toast.success('Sugerencia enviada correctamente');
      setForm({ type: 'sugerencia', title: '', description: '', priority: 'media' });
      onCreated();
      onClose();
    } catch {
      toast.error('Error al enviar la sugerencia');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Nueva sugerencia / reporte</DialogTitle>
          <DialogDescription>
            Comparte una idea, reporta un error o propón una mejora para el CRM.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium block mb-1">Tipo</label>
              <Select value={form.type} onValueChange={v => setForm(f => ({ ...f, type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sugerencia">Sugerencia</SelectItem>
                  <SelectItem value="bug">Error / Bug</SelectItem>
                  <SelectItem value="mejora">Mejora</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Prioridad</label>
              <Select value={form.priority} onValueChange={v => setForm(f => ({ ...f, priority: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="baja">Baja</SelectItem>
                  <SelectItem value="media">Media</SelectItem>
                  <SelectItem value="alta">Alta</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Título *</label>
            <input
              className="w-full border border-input rounded-md px-3 py-2 text-sm bg-background text-foreground"
              placeholder="Describe brevemente..."
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            />
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Descripción *</label>
            <textarea
              className="w-full border border-input rounded-md px-3 py-2 text-sm bg-background text-foreground resize-none"
              rows={4}
              placeholder="Explica con detalle tu sugerencia o el error encontrado..."
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button
            disabled={saving}
            className="bg-[#2d8659] hover:bg-[#2d8659]/90"
            onClick={handleSubmit}
          >
            {saving ? 'Enviando...' : 'Enviar sugerencia'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Página principal ─────────────────────────────────────────────────
export function SugerenciasPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterType) params.type = filterType;
      const res = await axios.get(`${API}/feedback`, { params });
      setItems(Array.isArray(res.data) ? res.data : res.data.items || []);
    } catch {
      toast.error('Error al cargar sugerencias');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterType]);

  useEffect(() => { load(); }, [load]);

  async function handleStatusChange(id, status, adminNotes) {
    try {
      await axios.put(`${API}/feedback/${id}`, { status, admin_notes: adminNotes });
      toast.success('Sugerencia actualizada');
      load();
    } catch {
      toast.error('Error al actualizar');
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('¿Eliminar esta sugerencia?')) return;
    try {
      await axios.delete(`${API}/feedback/${id}`);
      toast.success('Eliminado');
      load();
    } catch {
      toast.error('Error al eliminar');
    }
  }

  const counts = {
    pendiente:    items.filter(i => i.status === 'pendiente').length,
    en_revision:  items.filter(i => i.status === 'en_revision').length,
    implementado: items.filter(i => i.status === 'implementado').length,
    rechazado:    items.filter(i => i.status === 'rechazado').length,
  };

  return (
    <div className="space-y-6" data-testid="sugerencias-page">

      {/* ── Header ──────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Sugerencias</h1>
          <p className="text-muted-foreground">
            {isAdmin
              ? 'Gestiona las sugerencias y reportes del equipo'
              : 'Envía sugerencias, reporta errores o propón mejoras'}
          </p>
        </div>
        <Button
          className="bg-[#2d8659] hover:bg-[#2d8659]/90"
          onClick={() => setShowNew(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Nueva sugerencia
        </Button>
      </div>

      {/* ── KPI cards (admin only) ───────────────────────────────────── */}
      {isAdmin && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KpiCard
            title="Pendientes"
            value={counts.pendiente}
            icon={Clock}
            color="accent"
            active={filterStatus === 'pendiente'}
            onClick={() => setFilterStatus(s => s === 'pendiente' ? '' : 'pendiente')}
          />
          <KpiCard
            title="En revisión"
            value={counts.en_revision}
            icon={RefreshCw}
            color="warning"
            active={filterStatus === 'en_revision'}
            onClick={() => setFilterStatus(s => s === 'en_revision' ? '' : 'en_revision')}
          />
          <KpiCard
            title="Implementados"
            value={counts.implementado}
            icon={CheckCircle2}
            color="secondary"
            active={filterStatus === 'implementado'}
            onClick={() => setFilterStatus(s => s === 'implementado' ? '' : 'implementado')}
          />
          <KpiCard
            title="Rechazados"
            value={counts.rechazado}
            icon={XCircle}
            color="primary"
            active={filterStatus === 'rechazado'}
            onClick={() => setFilterStatus(s => s === 'rechazado' ? '' : 'rechazado')}
          />
        </div>
      )}

      {/* ── Filtros ──────────────────────────────────────────────────── */}
      <div className="flex gap-3 flex-wrap items-center">
        <Select value={filterType || 'all'} onValueChange={v => setFilterType(v === 'all' ? '' : v)}>
          <SelectTrigger className="h-8 w-40 text-sm">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los tipos</SelectItem>
            <SelectItem value="sugerencia">Sugerencia</SelectItem>
            <SelectItem value="bug">Error / Bug</SelectItem>
            <SelectItem value="mejora">Mejora</SelectItem>
          </SelectContent>
        </Select>

        {!isAdmin && (
          <Select value={filterStatus || 'all'} onValueChange={v => setFilterStatus(v === 'all' ? '' : v)}>
            <SelectTrigger className="h-8 w-40 text-sm">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {(filterStatus || filterType) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setFilterStatus(''); setFilterType(''); }}
          >
            Limpiar filtros
          </Button>
        )}
      </div>

      {/* ── Tabla / empty state ──────────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {items.length} sugerencia{items.length !== 1 ? 's' : ''}
            {filterStatus ? ` · ${STATUS_CONFIG[filterStatus]?.label}` : ''}
            {filterType   ? ` · ${TYPE_CONFIG[filterType]?.label}` : ''}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1a365d]" />
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-center px-4">
              <Lightbulb className="h-12 w-12 text-muted-foreground/30" />
              <div>
                <p className="font-medium text-muted-foreground">No hay sugerencias todavía</p>
                <p className="text-sm text-muted-foreground/70 mt-1">
                  {filterStatus || filterType
                    ? 'Intenta cambiar los filtros para ver más resultados'
                    : 'Sé el primero en enviar una sugerencia o reportar un error'}
                </p>
              </div>
              {!(filterStatus || filterType) && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-1"
                  onClick={() => setShowNew(true)}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Crear primera sugerencia
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[120px]">Tipo</TableHead>
                  <TableHead>Título / Descripción</TableHead>
                  <TableHead className="w-[90px]">Prioridad</TableHead>
                  <TableHead className="w-[120px]">Estado</TableHead>
                  <TableHead className="w-[140px]">Autor</TableHead>
                  <TableHead className="w-[110px]">Fecha</TableHead>
                  <TableHead className="w-[80px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map(item => (
                  <SugerenciaRow
                    key={item.id}
                    item={item}
                    isAdmin={isAdmin}
                    onStatusChange={handleStatusChange}
                    onDelete={handleDelete}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <NewFeedbackModal
        open={showNew}
        onClose={() => setShowNew(false)}
        onCreated={load}
      />
    </div>
  );
}
